import { lazy, useEffect } from "react";
import { RouteSuspense } from "@/shared/routing/RouteSuspense";
import type { RouteObject } from "react-router-dom";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getSellerPath } from "@/lib/routing/portal-urls";

import { useLocale } from "@seller/app/locale";

const CategoriesPage = lazy(() => import("@/modules/categories/pages/CategoriesPage").then(module => ({ default: module.CategoriesPage })));

function SellerCategoriesRoute() {
  const locale = useLocale();
  const { storeSlug = "" } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const notice = searchParams.get("notice") ?? "";
  const showSaveNotice = notice === "category_saved";
  const defaultCreateOpen = searchParams.get("create") === "1";
  // The setup helper sends sellers here to be SHOWN the button, not to have it
  // pressed for them (see useDashboardSetupGuide).
  const highlightAdd = searchParams.get("guide") === "add";
  const defaultEditId = searchParams.get("edit");

  return (
    <CategoriesPage
      locale={locale}
      storeSlug={storeSlug}
      showSaveNotice={showSaveNotice}
      defaultCreateOpen={defaultCreateOpen}
      defaultEditId={defaultEditId}
      highlightAdd={highlightAdd}
    />
  );
}

function SellerCategoryCreateRedirectRoute() {
  const locale = useLocale();
  const { storeSlug = "" } = useParams();
  const navigate = useNavigate();
  const target = `${getSellerPath(locale, storeSlug)}/categories?create=1`;

  useEffect(() => {
    navigate(target, { replace: true });
  }, [navigate, target]);

  return null;
}

function SellerCategoryEditRedirectRoute() {
  const locale = useLocale();
  const { storeSlug = "", id = "" } = useParams();
  const navigate = useNavigate();
  const target = `${getSellerPath(locale, storeSlug)}/categories?edit=${encodeURIComponent(id)}`;

  useEffect(() => {
    navigate(target, { replace: true });
  }, [navigate, target]);

  return null;
}

export const categoriesRoutes: RouteObject[] = [
  {
    path: "categories",
    element: <RouteSuspense><SellerCategoriesRoute /></RouteSuspense>,
  },
  {
    path: "categories/new",
    element: <RouteSuspense><SellerCategoryCreateRedirectRoute /></RouteSuspense>,
  },
  {
    path: "categories/:id",
    element: <RouteSuspense><SellerCategoryEditRedirectRoute /></RouteSuspense>,
  },
];
